import type { ValueOf } from 'type-fest';
import type { Brand } from './types';
import { initZanalytics as initZillowAnalytics, isInitialized } from '@zillow/universal-analytics';
import { getCurrentEnvForClientSide, PRODUCTION } from '@zg-rentals/environment-utils/client';
import { hasDeclinedFunctionalConsent } from '@zg-rentals/datagrail';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { logError } from '@zg-rentals/log-error';
import { clientOnly, getAnalyticsErrorType, getCountName, getLogPrefix } from './utils';

const API_HOSTS = {
  prod: 'e.zg-api.com',
  test: 'analytics-zg-api.test.zillow.net',
} as const;

const BRAND_SPECIFIC_FIELDS = {
  zillow: {
    apiKey: { prod: 'z_prod', test: 'z_test' },
    writeKey: { prod: 'zrm_prod_web', test: 'zrm_dev_web' },
  },
  hotpads: {
    apiKey: { prod: 'hp_prod_web', test: 'hp_dev_web' },
    writeKey: { prod: 'hp_prod_web_nl', test: 'hp_dev_web_nl' },
  },
} as const;

function createConfig(brandedFields: ValueOf<typeof BRAND_SPECIFIC_FIELDS>) {
  const isProd = getCurrentEnvForClientSide() === PRODUCTION;
  return {
    apiKey: isProd ? brandedFields.apiKey.prod : brandedFields.apiKey.test,
    apiHost: isProd ? API_HOSTS.prod : API_HOSTS.test,
    secure: true,
    writeKey: isProd ? brandedFields.writeKey.prod : brandedFields.writeKey.test,
  };
}

function getAnalyticsConfig(brand: keyof typeof BRAND_SPECIFIC_FIELDS) {
  return {
    [brand]: createConfig(BRAND_SPECIFIC_FIELDS[brand]),
  };
}

type ConsentWindow = Window & {
  'disable-analytics'?: boolean;
};

// Mirrors page-frame-content's zillowAnalytics.js; returns true when analytics is disabled (the SDK no-ops every call while `disable-analytics` is set)
function applyConsentFlag() {
  const consentWindow = window as ConsentWindow;

  if (hasDeclinedFunctionalConsent()) {
    consentWindow['disable-analytics'] = true;
  } else if (consentWindow['disable-analytics'] === undefined) {
    consentWindow['disable-analytics'] = false;
  }

  return Boolean(consentWindow['disable-analytics']);
}

// gives @zillow/universal-analytics a callable window.ua so calls are dropped silently instead of retried and logged as dropped
function stubUa() {
  const uaWindow = window as Window & { ua?: () => void };

  uaWindow.ua ??= () => {};
}

export const initZanalytics = clientOnly((brand: Brand = 'zillow') => {
  try {
    if (isInitialized()) return;

    if (applyConsentFlag()) {
      stubUa();
      getGlobalMonitor()?.count({ name: getCountName('initZanalytics consentDeclined') });
      return;
    }

    initZillowAnalytics(window, document, getAnalyticsConfig(brand), 'zanalytics');
    // useful for monitoring/debugging as these counts should match `browser init`
    getGlobalLogger()?.debug(getLogPrefix('zanalytics initialized'), { brand });
    getGlobalMonitor()?.count({ name: getCountName('initZanalytics') });
  } catch (error) {
    logError({ error, errorType: getAnalyticsErrorType('InitZanalytics') });
  }
});
